<script setup>
import SmileyIcon from '@iconify-vue/ph/smiley';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { unquoteSpreadsheetCell } from '@js/utils/string';
import {
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const model = defineModel();

const emojiCategories = [
    {
        name: 'Smileys',
        emojis: [
            { e: '😀', k: 'sourire heureux joie grin happy' },
            { e: '😃', k: 'sourire grand heureux big smile happy' },
            { e: '😄', k: 'sourire yeux rire smile eyes happy' },
            { e: '😁', k: 'sourire dents grin teeth happy' },
            { e: '😆', k: 'rire mdr laugh squint' },
            { e: '😅', k: 'rire sueur nerveux sweat laugh' },
            { e: '🤣', k: 'mdr rire sol rofl laugh' },
            { e: '😂', k: 'rire larmes joie laugh tears joy' },
            { e: '🙂', k: 'sourire leger slight smile' },
            { e: '😉', k: 'clin oeil wink' },
            { e: '😊', k: 'sourire rougir blush smile' },
            { e: '😇', k: 'ange innocent angel halo' },
            { e: '🥰', k: 'amour coeurs love hearts' },
            { e: '😍', k: 'amour yeux coeurs love eyes hearts' },
            { e: '🤩', k: 'etoiles excite star struck excited' },
            { e: '😘', k: 'bisou kiss coeur heart' },
            { e: '😋', k: 'miam delicieux savoureux yummy delicious' },
            { e: '😛', k: 'langue tirer tongue out' },
            { e: '😜', k: 'langue clin oeil wink tongue' },
            { e: '🤪', k: 'fou zinzin crazy zany' },
            { e: '😝', k: 'langue ferme yeux tongue closed eyes' },
            { e: '🤑', k: 'argent dollar money rich' },
            { e: '🤗', k: 'calin hug embrace' },
            { e: '🤭', k: 'oups rire cache giggle oops' },
            { e: '🤔', k: 'reflechir penser think hmm' },
            { e: '🤐', k: 'bouche ferme zipper silence shut' },
            { e: '😐', k: 'neutre neutral flat' },
            { e: '😑', k: 'ennui blasé expressionless bored' },
            { e: '😶', k: 'silence muet sans bouche mute' },
            { e: '😏', k: 'narquois smirk sly' },
            { e: '😒', k: 'agace ennui unamused annoyed' },
            { e: '🙄', k: 'yeux ciel eye roll annoyed' },
            { e: '😬', k: 'grimace awkward cringe' },
            { e: '😮‍💨', k: 'soupir exhale sigh relief' },
            { e: '🤥', k: 'menteur nez pinocchio liar' },
            { e: '😌', k: 'soulage calme relieved peaceful' },
            { e: '😔', k: 'triste pensif pensive sad' },
            { e: '😪', k: 'fatigue endormi sleepy tired' },
            { e: '🤤', k: 'bave drool' },
            { e: '😴', k: 'dormir sommeil sleep zzz' },
            { e: '😷', k: 'masque malade mask sick' },
            { e: '🤒', k: 'malade fievre thermometre sick fever' },
            { e: '🤕', k: 'blesse bandage hurt injured' },
            { e: '🤢', k: 'nausee degout nauseated sick green' },
            { e: '🤮', k: 'vomir vomit puke sick' },
            { e: '🥵', k: 'chaud brulant hot overheated' },
            { e: '🥶', k: 'froid gele cold freezing' },
            { e: '🥴', k: 'etourdi groggy woozy drunk' },
            { e: '😵', k: 'etourdi assomme dizzy knocked out' },
            { e: '🤯', k: 'explosion tete mind blown shocked' },
            { e: '🥳', k: 'fete celebration party celebrate' },
            { e: '🥸', k: 'deguise disguise incognito' },
            { e: '😎', k: 'cool lunettes sunglasses' },
            { e: '🤓', k: 'nerd geek intello lunettes' },
            { e: '🧐', k: 'monocle inspect curious' },
            { e: '😕', k: 'confus perplexe confused' },
            { e: '😟', k: 'inquiet worried concerned' },
            { e: '🙁', k: 'triste decu frown sad' },
            { e: '😮', k: 'surpris bouche ouverte surprised open mouth' },
            { e: '😯', k: 'surpris hushed stunned' },
            { e: '😲', k: 'choque etonne astonished shocked' },
            { e: '😳', k: 'rougir gene flushed embarrassed' },
            { e: '🥺', k: 'suppliant pitie pleading puppy eyes' },
            { e: '😢', k: 'pleurer larme crying tear sad' },
            { e: '😭', k: 'pleurer fort loud crying sobbing' },
            { e: '😤', k: 'enervé furieux steam angry huff' },
            { e: '😠', k: 'colere fache angry mad' },
            { e: '😡', k: 'rage furieux enrage angry red' },
            { e: '🤬', k: 'insulte juron censure swearing cursing' },
            { e: '😈', k: 'diable demon malicieux devil smiling' },
            { e: '👿', k: 'diable demon colere devil angry' },
            { e: '💀', k: 'crane mort skull dead death' },
        ],
    },
    {
        name: 'Gestes',
        emojis: [
            { e: '👋', k: 'salut coucou bonjour wave hello bye' },
            { e: '🤚', k: 'main levee raised hand stop' },
            { e: '🖐️', k: 'main doigts ouverts hand fingers splayed' },
            { e: '✋', k: 'main stop high five' },
            { e: '🖖', k: 'vulcain spock vulcan salute' },
            { e: '👌', k: 'ok parfait okay perfect' },
            { e: '🤌', k: 'pincement italien pinched fingers italian' },
            { e: '🤏', k: 'petit peu pinch small tiny' },
            { e: '✌️', k: 'victoire paix peace victory' },
            { e: '🤞', k: 'chance croiser doigts crossed fingers luck' },
            { e: '🤟', k: 'amour love you rock' },
            { e: '🤘', k: 'rock metal horns' },
            { e: '🤙', k: 'appeler telephone call me shaka' },
            { e: '👈', k: 'gauche pointer left point' },
            { e: '👉', k: 'droite pointer right point' },
            { e: '👆', k: 'haut pointer up point' },
            { e: '👇', k: 'bas pointer down point' },
            { e: '☝️', k: 'index haut one up point' },
            { e: '👍', k: 'pouce haut like bien thumb up good' },
            { e: '👎', k: 'pouce bas dislike nul thumb down bad' },
            { e: '✊', k: 'poing leve raised fist' },
            { e: '👊', k: 'poing cogner punch fist bump' },
            { e: '🤛', k: 'poing gauche left fist bump' },
            { e: '🤜', k: 'poing droite right fist bump' },
            { e: '👏', k: 'applaudir bravo clap applause' },
            { e: '🙌', k: 'mains levees celebration raise hands hooray' },
            { e: '👐', k: 'mains ouvertes open hands' },
            { e: '🤲', k: 'paumes haut palms up together' },
            { e: '🤝', k: 'poignee main handshake deal accord' },
            { e: '🙏', k: 'prier merci svp pray please thanks' },
            { e: '💪', k: 'muscle force biceps strong flex' },
            { e: '🦾', k: 'bras mecanique robot mechanical arm' },
        ],
    },
    {
        name: 'Coeurs',
        emojis: [
            { e: '❤️', k: 'coeur rouge amour red heart love' },
            { e: '🧡', k: 'coeur orange heart' },
            { e: '💛', k: 'coeur jaune yellow heart' },
            { e: '💚', k: 'coeur vert green heart' },
            { e: '💙', k: 'coeur bleu blue heart' },
            { e: '💜', k: 'coeur violet purple heart' },
            { e: '🖤', k: 'coeur noir black heart' },
            { e: '🤍', k: 'coeur blanc white heart' },
            { e: '🤎', k: 'coeur marron brown heart' },
            { e: '💔', k: 'coeur brise broken heart sad' },
            { e: '❤️‍🔥', k: 'coeur feu flamme fire heart passion' },
            { e: '💕', k: 'deux coeurs two hearts love' },
            { e: '💞', k: 'coeurs tournent revolving hearts love' },
            { e: '💓', k: 'coeur battant beating heart love' },
            { e: '💗', k: 'coeur grandissant growing heart love' },
            { e: '💖', k: 'coeur etincelle sparkling heart love' },
            { e: '💘', k: 'coeur fleche cupid heart arrow love' },
            { e: '💝', k: 'coeur ruban ribbon heart gift love' },
            { e: '💟', k: 'coeur decoration heart decoration love' },
            { e: '♥️', k: 'coeur carte suit heart love' },
            { e: '🫶', k: 'mains coeur hand heart love' },
        ],
    },
    {
        name: 'Objets',
        emojis: [
            { e: '⭐', k: 'etoile star favori' },
            { e: '🌟', k: 'etoile brillante glowing star bright' },
            { e: '✨', k: 'etincelles sparkles magic' },
            { e: '⚡', k: 'eclair foudre lightning bolt energy' },
            { e: '🔥', k: 'feu flamme fire hot trending' },
            { e: '💥', k: 'explosion boom collision crash' },
            { e: '🎉', k: 'fete confetti party tada celebration' },
            { e: '🎊', k: 'confetti balle party ball celebration' },
            { e: '🏆', k: 'trophee coupe trophy cup winner' },
            { e: '🥇', k: 'medaille or gold medal first' },
            { e: '🥈', k: 'medaille argent silver medal second' },
            { e: '🥉', k: 'medaille bronze medal third' },
            { e: '🎯', k: 'cible bullseye target dart goal' },
            { e: '🚀', k: 'fusee lancement rocket launch startup' },
            { e: '💡', k: 'ampoule idee light bulb idea' },
            { e: '📌', k: 'punaise epingle pushpin pin' },
            { e: '📎', k: 'trombone paperclip attachment' },
            { e: '📊', k: 'graphique barres chart bar graph stats' },
            { e: '📈', k: 'graphique hausse chart up trending growth' },
            { e: '📉', k: 'graphique baisse chart down trending decline' },
            { e: '✅', k: 'valide coche check mark done ok' },
            { e: '❌', k: 'croix erreur faux cross mark error wrong' },
            { e: '⚠️', k: 'attention avertissement warning alert danger' },
            { e: '💬', k: 'bulle message commentaire speech bubble comment' },
            { e: '👁️', k: 'oeil voir regarder eye see watch' },
            { e: '🔍', k: 'loupe chercher recherche search magnify' },
            { e: '🔗', k: 'lien chaine link chain url' },
            { e: '📧', k: 'email mail courrier envelope' },
            { e: '📞', k: 'telephone appel phone call' },
            { e: '🕐', k: 'horloge heure temps clock time' },
            { e: '📅', k: 'calendrier date calendar schedule' },
            { e: '💰', k: 'argent sac money bag dollar rich' },
        ],
    },
];

const {
    fieldId,
    fieldName,
    label,
    placeholder,
    legend,
    rows = 5,
    disabled = false,
    required = false,
    resize = 'both',
    highlight = null,
    emojiPicker = false,
    fill = false,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: false,
    },
    placeholder: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
    rows: {
        type: [Number, String],
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    resize: {
        type: String,
        required: false,
    },
    highlight: {
        type: Array,
        required: false,
    },
    emojiPicker: {
        type: Boolean,
        required: false,
    },
    fill: {
        type: Boolean,
        required: false,
    },
});

const backdrop = useTemplateRef('backdrop');

const emojiSearch = ref('');

const textareaEl = useTemplateRef('textarea');

const highlightedText = computed(() => {
    if (!model.value) return '';

    let safeText = escapeHTML(model.value);

    if (highlight && Array.isArray(highlight)) {
        highlight.forEach((regex) => {
            safeText = safeText.replace(
                regex,
                '<span class="bg-yellow-300 text-black">$1</span>',
            );
        });
    }

    if (model.value[model.value.length - 1] === '\n') {
        safeText += '<br>';
    }

    return safeText;
});

const filteredCategories = computed(() => {
    const query = emojiSearch.value.toLowerCase().trim();
    if (!query) return emojiCategories;

    const words = query.split(/\s+/);

    return emojiCategories
        .map((cat) => ({
            ...cat,
            emojis: cat.emojis.filter((item) =>
                words.every((w) => item.k.includes(w)),
            ),
        }))
        .filter((cat) => cat.emojis.length > 0);
});

function handleScroll(event) {
    if (backdrop.value) {
        backdrop.value.scrollTop = event.target.scrollTop;
        backdrop.value.scrollLeft = event.target.scrollLeft;
    }
}

function escapeHTML(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function handlePaste(event) {
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    const cleanedText = unquoteSpreadsheetCell(pastedText);

    if (cleanedText === pastedText) {
        return;
    }

    event.preventDefault();

    if (document.execCommand('insertText', false, cleanedText)) {
        return;
    }

    const textarea = event.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = model.value || '';

    model.value = text.slice(0, start) + cleanedText + text.slice(end);

    nextTick(() => {
        const position = start + cleanedText.length;
        textarea.setSelectionRange(position, position);
    });
}

function insertEmoji(emoji) {
    const textarea = textareaEl.value?.$el || textareaEl.value;
    if (!textarea) {
        model.value = (model.value || '') + emoji;
        return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = model.value || '';

    model.value = text.slice(0, start) + emoji + text.slice(end);

    nextTick(() => {
        const pos = start + emoji.length;
        textarea.selectionStart = pos;
        textarea.selectionEnd = pos;
        textarea.focus();
    });
}
</script>

<template>
    <div class="mb-4 flex flex-col" :class="{ 'min-h-0 flex-1': fill }">
        <label v-if="label" :for="fieldName" class="mb-2">
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </label>

        <div class="relative" :class="{ 'min-h-0 flex-1': fill }">
            <div
                v-if="highlight"
                ref="backdrop"
                class="pointer-events-none absolute top-0 left-0 z-[1] size-full overflow-auto rounded-sm border border-transparent px-5 py-3 break-words whitespace-pre-wrap text-transparent"
                v-html="highlightedText"
            ></div>

            <Field
                ref="textarea"
                :id="fieldId"
                :name="fieldName"
                as="textarea"
                :rows="rows"
                :disabled="disabled"
                :placeholder="placeholder"
                v-model="model"
                class="relative z-5 block w-full rounded-sm border border-gray-300 bg-white px-5 py-3 ring-1 ring-transparent duration-150 placeholder:text-gray-400 focus:ring-gray-300 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100"
                :class="{
                    'pt-6':
                        $slots['top-left-actions'] ||
                        $slots['top-right-actions'],
                    'pb-6':
                        emojiPicker ||
                        $slots['bottom-left-actions'] ||
                        $slots['bottom-right-actions'],
                    'h-full': fill,
                }"
                :style="{ resize: fill ? 'none' : resize }"
                @scroll="handleScroll"
                @paste="handlePaste"
            />

            <div
                v-if="$slots['top-left-actions']"
                class="absolute -top-1.5 left-5 z-10 flex items-center gap-2"
            >
                <slot name="top-left-actions" />
            </div>

            <div
                v-if="$slots['top-right-actions']"
                class="absolute -top-1.5 right-5 z-10 flex items-center gap-2"
            >
                <slot name="top-right-actions" />
            </div>

            <div
                v-if="emojiPicker || $slots['bottom-left-actions']"
                class="absolute -bottom-1.5 left-5 z-10 flex items-center gap-2"
            >
                <PopoverRoot v-if="emojiPicker">
                    <PopoverTrigger as-child>
                        <ActionButton
                            type="secondary"
                            size="small"
                            :icon="SmileyIcon"
                            :disabled="disabled"
                        />
                    </PopoverTrigger>

                    <PopoverPortal>
                        <PopoverContent
                            side="top"
                            :side-offset="8"
                            align="start"
                            class="z-50 w-72 rounded-sm border border-gray-300 bg-white shadow-sm"
                            @open-auto-focus.prevent
                        >
                            <div class="border-b border-gray-100 p-2">
                                <input
                                    v-model="emojiSearch"
                                    type="text"
                                    placeholder="Rechercher..."
                                    class="w-full rounded border border-gray-300 px-3 py-1 text-sm ring-1 ring-transparent duration-150 placeholder:text-gray-400 focus:border-gray-300 focus:ring-gray-300 focus:outline-none"
                                />
                            </div>

                            <div class="max-h-56 overflow-y-auto p-2">
                                <template
                                    v-for="category in filteredCategories"
                                    :key="category.name"
                                >
                                    <div
                                        class="mb-1 text-xs font-medium text-gray-400"
                                    >
                                        {{ category.name }}
                                    </div>

                                    <div class="mb-2 grid grid-cols-8 gap-0.5">
                                        <button
                                            v-for="item in category.emojis"
                                            :key="item.e"
                                            type="button"
                                            :title="item.k"
                                            class="cursor-pointer rounded p-1 text-center text-lg hover:bg-gray-100"
                                            @click="insertEmoji(item.e)"
                                        >
                                            {{ item.e }}
                                        </button>
                                    </div>
                                </template>

                                <div
                                    v-if="filteredCategories.length === 0"
                                    class="py-4 text-center text-sm text-gray-400"
                                >
                                    Aucun emoji trouvé
                                </div>
                            </div>
                        </PopoverContent>
                    </PopoverPortal>
                </PopoverRoot>

                <slot name="bottom-left-actions" />
            </div>

            <div
                v-if="$slots['bottom-right-actions']"
                class="absolute right-5 -bottom-1.5 z-10 flex items-center gap-2"
            >
                <slot name="bottom-right-actions" />
            </div>
        </div>

        <em v-if="legend" class="mt-1 text-xs text-gray-400">
            {{ legend }}
        </em>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>
</template>
